import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/ignoreElements';
import 'rxjs/add/operator/sample';
import 'rxjs/add/operator/zip';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/map';
import type { ActionsObservable } from 'redux-observable';
import mergeWith from 'lodash/mergeWith';
import pickBy from 'lodash/pickBy';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/jira-analytics-web-react/src/utils/fire-track-event.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	convertProjectType,
	type AnalyticsProjectType,
} from '@atlassian/jira-project-config/src/analytics';
import { toErrorId, toErrorMessage } from '@atlassian/jira-project-settings-apps-common/src/types';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger';
import { PACKAGE_NAME } from '../../model/constants';
import type { Action } from '../../state/actions';
import { getProjectIdSelector, getProjectTypeSelector } from '../../state/context/selectors';
import {
	SAVE_PROJECT_DETAILS,
	SAVE_PROJECT_DETAILS_SUCCESS,
	SAVE_ALL_PROJECT_DETAILS,
	SAVE_ALL_PROJECT_DETAILS_SUCCESS,
} from '../../state/entities/actions';
import { formValuesSelector } from '../../state/entities/selectors';
import type { AppState } from '../../state/types';
import { formUiValuesSelector } from '../../state/ui/selectors';

type ActionWithAnalytics = {
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	};
};

type AnalyticsPayload = {
	action: string;
	actionSubjectId: string;
	objectType: string;
	objectId: string;
	containerType: string;
	containerId: string;
	tags: string[];
	attributes: {
		updatedItems: string; // analytics do not allow anything but strings!,
		projectType: AnalyticsProjectType;
		simplified: boolean;
		releasedProjectKeys?: {
			numberOfPreviousKeys: string;
			numberOfReleasedKeys: string;
		};
	};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildDiff = (oldV: any, newV: any) => oldV !== newV;

/**
 * Compare UI state and App state to find out which values are changed so we can send that in analytics
 */
const computeUpdatedItems =
	(store: MiddlewareAPI<AppState>) => (): /* action: not used */ string[] => {
		const state: AppState = store.getState();
		const oldValues = formValuesSelector(state);
		const newValues = formUiValuesSelector(state);

		const diff = mergeWith(oldValues, newValues, buildDiff);
		const updatedKeys = Object.keys(pickBy(diff));

		return updatedKeys;
	};

/**
 * Prepare analytics payload object that we can feed into fireTrackAnalytics
 */
const computeAnalyticsPayload =
	(store: MiddlewareAPI<AppState>) =>
	(updatedItems: string[]): AnalyticsPayload => {
		const state: AppState = store.getState();
		const analyticsPayload: AnalyticsPayload = {
			action: 'updated',
			containerType: 'project',
			actionSubjectId: String(getProjectIdSelector(state)),
			objectType: 'project',
			objectId: String(getProjectIdSelector(state)),
			containerId: String(getProjectIdSelector(state)),
			tags: ['donut-world', 'project-details'],
			attributes: {
				projectType: convertProjectType(getProjectTypeSelector(state)),
				updatedItems: `[${updatedItems.join(',')}]`,
				simplified: true,
			},
		};

		if (fg('hix-4189-releasing-historical-project-keys')) {
			const { releasedProjectKeys, historicalProjectKeys } = state.ui;
			if (historicalProjectKeys?.length > 0) {
				analyticsPayload.attributes.releasedProjectKeys = {
					numberOfPreviousKeys: historicalProjectKeys.length.toString(),
					numberOfReleasedKeys: releasedProjectKeys.length.toString(),
				};
			}
		}

		return analyticsPayload;
	};

/**
 * Trigger the analytics AJAX call
 */
const sendAnalytics = ([analyticsEvent, analyticsPayload]: [
	UIAnalyticsEvent,
	AnalyticsPayload,
]) => {
	// try ... catch is necessary because fireTrackAnalytics may throw Errors and we never want those to affect user
	try {
		fireTrackAnalytics(analyticsEvent, analyticsPayload);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logError(
			PACKAGE_NAME,
			toErrorId('analytics.projectUpdated'),
			toErrorMessage('Failed to send analytics event for project updated'),
			error,
		);
	}
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action>, store: MiddlewareAPI<AppState>) => {
	// These are all the ways how a Project Details save can happen.
	// Think of this as "Save button click" stream, except more complicated.
	const saveStart$ = action$.ofType(
		// ALL project details will be alone
		SAVE_ALL_PROJECT_DETAILS, // when project is simplified
		// these two can happen as one, or other, or both
		SAVE_PROJECT_DETAILS,
	);

	// Turn stream of "Save button clicks" into stream of analyticsPayloads.
	const analyticsPayload$ = saveStart$
		.map(computeUpdatedItems(store))
		.map(computeAnalyticsPayload(store));

	// Turn stream of "Save button clicks" into stream of AnalyticsEvents.
	// The events may repeat (because the details + accessLevel love story), so we need distinctUntilChanged to avoid sending duplicate data.
	const analyticsEvent$ = saveStart$
		.map((action: ActionWithAnalytics) => action.meta.analyticsEvent)
		.distinctUntilChanged();

	// We need both payload and event to send the track event, so let's turn that into single stream.
	// This stream has all the data we need to send the event, but emits too early. We must wait until success.
	const analyticsData$ = analyticsEvent$.zip(analyticsPayload$);

	// These are all the events where we want to actually send the analytics. On failure, nothing happens!
	// This stream has the timing we need but not the data we need.
	const saveSuccess$ = action$.ofType(
		SAVE_PROJECT_DETAILS_SUCCESS,
		SAVE_ALL_PROJECT_DETAILS_SUCCESS,
	);

	// Sample is nice because it gives us a new stream; it emits when the success$ emits, but gives data from the analyticsData$!
	// This stream has the correct timing, and all the data.
	const analyticsTrigger$ = analyticsData$.sample(saveSuccess$);

	// Actually send the analytics and then ignore whatever comes out; we do not want to dispatch any more Redux actions from here.
	return analyticsTrigger$.do(sendAnalytics).ignoreElements();
};
